import { useEffect } from 'react';
import i18next from 'i18next';
import type { i18n as I18n } from 'i18next';

export type CandidateDocumentTitleKey =
  | 'documentTitle.app'
  | 'documentTitle.jobBoard';

/**
 * Keeps <html lang> and the tab title in the candidate's language: applies them
 * now and again on every language change. Returns a function that stops
 * listening. Candidate entry points only: the recruiter app shares the i18n
 * instance, sets its own lang and keeps the static title.
 */
export const bindDocumentLocale = (
  i18n: I18n,
  titleKey: CandidateDocumentTitleKey
): (() => void) => {
  const apply = (): void => {
    document.documentElement.lang = i18n.resolvedLanguage ?? 'en';
    document.title = i18n.t(titleKey);
  };
  apply();
  i18n.on('languageChanged', apply);
  return () => i18n.off('languageChanged', apply);
};

/**
 * Binds <html lang> and the tab title for as long as the calling root is
 * mounted.
 */
export const useDocumentLocale = (
  titleKey: CandidateDocumentTitleKey
): void => {
  useEffect(() => bindDocumentLocale(i18next, titleKey), [titleKey]);
};
